import React, { useEffect, useMemo, useRef, useState } from "react";
import { GeoJSON, MapContainer, TileLayer, ZoomControl, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { PostCode } from "./services/listingsSearchCriteriaApi";
import {
    buildPostcodeIndex,
    isEffectivelySelected,
    isPartiallySelected,
    areaCodeOf, PostcodeSelectionState,
} from "./postcodeUtils";
import { brightColorFromId, colorFromId, geoAreaContains } from "./mapUtils";

type Props = {
    postcodes: PostCode[];
    selectionState: PostcodeSelectionState;
    onToggle: (code: string) => void;
    initialCenter?: [number, number];
    initialZoom?: number;
};

const ZOOM_AREA_MAX = 10;       // zoom <= 10: AREA
const ZOOM_DISTRICT_MAX = 13;   // zoom 11-13: DISTRICT
                                // zoom > 13: SECTOR

function visibleLevel(zoom: number): PostCode["level"] {
    if (zoom <= ZOOM_AREA_MAX) return "AREA";
    if (zoom <= ZOOM_DISTRICT_MAX) return "DISTRICT";
    return "SECTOR";
}

function AttributionPrefix() {
    const map = useMap();
    useEffect(() => {
        map.attributionControl.setPrefix('<a href="https://leafletjs.com">Leaflet</a>');
    }, [map]);
    return null;
}

function InvalidateSize() {
    const map = useMap();
    useEffect(() => {
        const observer = new ResizeObserver(() => map.invalidateSize());
        observer.observe(map.getContainer());
        return () => observer.disconnect();
    }, [map]);
    return null;
}

function ZoomTracker({ onZoomChange }: { onZoomChange: (z: number) => void }) {
    const map = useMap();
    useEffect(() => {
        const handler = () => onZoomChange(map.getZoom());
        map.on("zoomend", handler);
        return () => { map.off("zoomend", handler); };
    }, [map, onZoomChange]);
    return null;
}

function FitToFeatureCollection({ fc }: { fc: any }) {
    const map = useMap();
    useEffect(() => {
        if (!fc?.features?.length) return;
        const bounds = L.geoJSON(fc).getBounds();
        if (bounds.isValid()) map.fitBounds(bounds.pad(0.06));
    }, [fc, map]);
    return null;
}

function PostcodeTouchHighlighter({ postcodes, onToggle }: {
    postcodes: PostCode[];
    onToggle: (code: string) => void;
}) {
    const map = useMap();
    const ref = useRef({ postcodes, onToggle });
    useEffect(() => { ref.current = { postcodes, onToggle }; });

    useEffect(() => {
        const handler = (e: L.LeafletMouseEvent) => {
            const p: [number, number] = [e.latlng.lng, e.latlng.lat];
            for (const pc of ref.current.postcodes) {
                if (geoAreaContains(pc.geoArea, p)) {
                    ref.current.onToggle(pc.code);
                    return;
                }
            }
        };
        map.on("click", handler);
        return () => { map.off("click", handler); };
    }, [map]);

    return null;
}

function selectionFingerprint(state: PostcodeSelectionState): string {
    return Array.from(state.selectedCodes).sort().join(",");
}

export default function PostcodeMap({
    postcodes,
    selectionState,
    onToggle,
    initialCenter = [51.5074, -0.1278],
    initialZoom = 10,
}: Props) {
    const isTouch = useMemo(() => window.matchMedia("(pointer: coarse)").matches, []);
    const [zoom, setZoom] = useState(initialZoom);

    const level = visibleLevel(zoom);

    const visiblePostcodes = useMemo(
        () => postcodes.filter(p => p.level === level),
        [postcodes, level],
    );

    const fc = useMemo(() => {
        if (!visiblePostcodes.length) return null;
        return {
            type: "FeatureCollection" as const,
            features: visiblePostcodes.map(p => ({
                type: "Feature" as const,
                properties: { code: p.code },
                geometry: p.geoArea as any,
            })),
        };
    }, [visiblePostcodes]);

    // Fit bounds once when AREA-level postcodes first load
    const areaFc = useMemo(() => {
        const areas = postcodes.filter(p => p.level === "AREA");
        if (!areas.length) return null;
        return {
            type: "FeatureCollection" as const,
            features: areas.map(p => ({
                type: "Feature" as const,
                properties: {},
                geometry: p.geoArea as any,
            })),
        };
    }, [postcodes]);

    const postcodeIndex = useMemo(
        () => buildPostcodeIndex(postcodes),
        [postcodes],
    );

    const fingerprint = selectionFingerprint(selectionState);

    return (
        <div style={{ height: "100%", width: "100%", position: "relative" }}>
            <MapContainer
                center={initialCenter}
                zoom={initialZoom}
                minZoom={8}
                zoomControl={false}
                style={{ height: "100%", width: "100%" }}
            >
                <ZoomControl position="bottomleft" />
                <AttributionPrefix />
                <InvalidateSize />
                <ZoomTracker onZoomChange={setZoom} />
                <TileLayer
                    url={`https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${process.env.REACT_APP_CARTO_API_KEY}`}
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                />

                {areaFc && <FitToFeatureCollection fc={areaFc} />}

                {isTouch && (
                    <PostcodeTouchHighlighter
                        postcodes={visiblePostcodes}
                        onToggle={onToggle}
                    />
                )}

                {fc && (
                    <GeoJSON
                        key={`${level}-${fingerprint}`}
                        data={fc as any}
                        style={(feature) => {
                            const code = feature?.properties?.code as string;
                            const selected = isEffectivelySelected(code, postcodeIndex, selectionState);
                            const partial = !selected && isPartiallySelected(code, postcodeIndex, selectionState);
                            const areaCode = areaCodeOf(code, postcodeIndex);
                            const baseColor = colorFromId(areaCode);
                            const brightColor = brightColorFromId(areaCode);

                            if (selected) {
                                return { fillColor: brightColor, fillOpacity: 0.65, color: brightColor, weight: 2.5, opacity: 1 };
                            }
                            if (partial) {
                                return { fillColor: baseColor, fillOpacity: 0.35, color: baseColor, weight: 2, opacity: 0.9 };
                            }
                            return { fillColor: baseColor, fillOpacity: 0.18, color: baseColor, weight: 1.5, opacity: 0.75 };
                        }}
                        onEachFeature={(feature, layer) => {
                            const code = feature.properties?.code as string;

                            if (isTouch) {
                                layer.on("add", () => {
                                    const path = (layer as any)._path as SVGElement | undefined;
                                    if (path) path.style.pointerEvents = "none";
                                });
                                return;
                            }

                            (layer as any).bindTooltip(code, { sticky: true });
                            layer.on("mouseover", () => {
                                const sel = isEffectivelySelected(code, postcodeIndex, selectionState);
                                const partial = !sel && isPartiallySelected(code, postcodeIndex, selectionState);
                                const areaCode = areaCodeOf(code, postcodeIndex);
                                const bright = brightColorFromId(areaCode);
                                const base = colorFromId(areaCode);
                                if (sel) {
                                    (layer as any).setStyle({ fillColor: bright, fillOpacity: 0.85, color: bright, weight: 3, opacity: 1 });
                                } else if (partial) {
                                    (layer as any).setStyle({ fillColor: base, fillOpacity: 0.55, color: base, weight: 2.5, opacity: 1 });
                                } else {
                                    (layer as any).setStyle({ fillColor: base, fillOpacity: 0.4, color: bright, weight: 2, opacity: 1 });
                                }
                            });
                            layer.on("mouseout", () => {
                                const sel = isEffectivelySelected(code, postcodeIndex, selectionState);
                                const partial = !sel && isPartiallySelected(code, postcodeIndex, selectionState);
                                const areaCode = areaCodeOf(code, postcodeIndex);
                                const bright = brightColorFromId(areaCode);
                                const base = colorFromId(areaCode);
                                if (sel) {
                                    (layer as any).setStyle({ fillColor: bright, fillOpacity: 0.65, color: bright, weight: 2.5, opacity: 1 });
                                } else if (partial) {
                                    (layer as any).setStyle({ fillColor: base, fillOpacity: 0.35, color: base, weight: 2, opacity: 0.9 });
                                } else {
                                    (layer as any).setStyle({ fillColor: base, fillOpacity: 0.18, color: base, weight: 1.5, opacity: 0.75 });
                                }
                            });
                            layer.on("click", () => onToggle(code));
                        }}
                    />
                )}
            </MapContainer>
        </div>
    );
}
